import { TranslateService } from "@ngx-translate/core";

import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  getCategoriesForListByUsage,
  getPropertiesForListByUsage,
} from "src/app/store/actions/parties/parties.actions";
import {
  AppState,
  getCardsExcelState,
  getLoadingCardsState,
  getTotalCardsState,
  LoadCardFilteredListState,
  loadedCategoriesListPartyState,
  loadedPropertiesListPartyState,
} from "src/app/store/reducers";

import { CardsDTO } from "src/app/models/Cards/cardDTO";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";

import { CHIP_STYLE, getChipStyle } from "../../utils/customTableUtils";
import { setCategories, setProperties } from "../../utils/tableUtils";
import { getCardTableConfig, saveCardTableConfig } from "src/app/store/actions/cards/cards.actions";

const USAGE_CODE = "CARD";

export class BaseCardsList {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  cardList: CardsDTO[];
  cardList$: Observable<CardsDTO[]>;
  excelCardList$: Observable<CardsDTO[]>;
  totalCards$: Observable<number>;

  loadedCardSub: Subscription;
  multiSubs: Subscription;

  categories$: Observable<CategoryDTO[]>;
  properties$: Observable<PropertyDTO[]>;

  formPath: string = "cards/card";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Cards.code",
      field: "code",
      fieldType: FieldType.STRING,
      identifier: true,
      searchable: true,
    },
    {
      header: "Cards.cardOverallStatusDescription",
      field: "cardOverallStatusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Cards.cardNumber",
      field: "cardNumber",
      fieldType: FieldType.STRING,
      hidden: true,
      searchable: true,
    },
    // {
    //   header: "Cards.customerNumber",
    //   field: "customerNumber",
    //   fieldType: FieldType.STRING,
    //   hidden: true,
    //   searchable: true,
    // },
    {
      header: "Cards.pan",
      field: "pan",
      fieldType: FieldType.STRING,
      hidden: true,
      searchable: true,
    },
    {
      header: "Cards.cardLayoutDescription",
      field: "cardLayoutDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.cardSystemDescription",
      field: "cardSystemDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.label",
      field: "label",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Cards.cardStatusDescription",
      field: "cardStatusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.name",
      field: "employee.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Cards.vatin",
      field: "employee.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.employeeStatusDescription",
      field: "employee.statusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    }, 
    {
      header: "Cards.customerCode",
      field: "customer.customerCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.customerVatin",
      field: "customer.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.customerName",
      field: "customer.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Cards.customerStatusDescription",
      field: "customer.statusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.contractCode",
      field: "contract.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.employeeCode",
      field: "employee.code",
      fieldType: FieldType.STRING,
    },
    {
      header: "Cards.contractDescription",
      field: "contract.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "Cards.vehicleCode",
      field: "vehicle.code",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.licensePlate",
      field: "vehicle.licensePlate",
      fieldType: FieldType.STRING,
    },
    {
      header: "Cards.description",
      field: "vehicle.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.vehicleStatusDescription",
      field: "vehicle.statusDescription",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Cards.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Cards.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(
    public store: Store<AppState>,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(getLoadingCardsState));
    this.cardList$ = this.store.pipe(select(LoadCardFilteredListState));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.excelCardList$ = this.store.pipe(select(getCardsExcelState));
    this.totalCards$ = this.store.pipe(select(getTotalCardsState));
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Cards")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }

  setTableCustomElements() {
    this.loadedCardSub = this.cardList$
      .pipe(skip(1))
      .subscribe((cards: any[]) => {
        this.cardList = cards;
        if (
          this.cardList !== undefined &&
          this.cardList !== null &&
          this.cardList?.length > 0
        ) {
          this.cardList = this.cardList.map((card) => {
            let treatedCard = card;
            treatedCard = this.setCustomStatusChip(treatedCard);
            return treatedCard;
          });
        }
      });
  }

  setCustomStatusChip(card: any) {
    const defaultCard = {
      ...card,
    };
    const { cardOverallStatusCode, cardOverallStatusDescription } = card;
    if (cardOverallStatusDescription === undefined) {
      return defaultCard;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;

    if (
      cardOverallStatusDescription !== undefined &&
      cardOverallStatusDescription !== null
    ) {
      if (
        cardOverallStatusCode !== undefined &&
        cardOverallStatusCode !== null
      ) {
        switch (cardOverallStatusCode) {
          case "ACTIVE":
            style = CHIP_STYLE.SUCCESS;
            break;

          //Status are missing!!
          case "DISABLED":
          case "CUSTOMER_INACTIVE":
          case "NOT_STARTED":
          case "EXPIRED":
            style = CHIP_STYLE.INACTIVE;
            break;

          case "LOW PLAFOND":
          case "CONTRACT_WARNING":
          case "EXPIRED":
            style = CHIP_STYLE.WARNING;
            break;

          case "BLOCKED":
          case "CUSTOMER_BLOCKED":
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      let styleSettings = getChipStyle(style);

      return {
        ...card,
        cardOverallStatusDescription: {
          value: cardOverallStatusDescription,
          style: styleSettings.style,
          icon: styleSettings.icon,
        },
      };
    }
  }

  getCardCategoriesProperties(usageCode: string, getCards: Function) {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig(usageCode);
      getCards();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getCardTableConfig({
        options: { code: usageCode, settingType: ConfigSettingType.TABLE_CONFIG },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getCards: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getCards(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getExportData(exportEvent: ExportEvent, getCardsExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getCardsExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveCardTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
